import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import useAccountPartProTrade from "../../data/useAccountPartProTrade";
import useNotification from "../notifications/useNotifications";
import Modal, { ModalProps } from "../../layouts/Modal";
import { IPartProTradeBuyerLink } from "../../models/partPro/IPartProTradeBuyer";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

interface AccountPartProTradeModalProps
  extends Omit<ModalProps, "children" | "width"> {
  accountId: string;
  /** Pre-fills the invite when nothing has been sent yet */
  defaultEmail?: string;
}

const formatDate = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : "";

function LinkSummary({ link }: { link: IPartProTradeBuyerLink }) {
  switch (link.status) {
    case "LINKED":
      return (
        <p className="text-sm text-gray-700">
          Linked to <span className="font-medium">{link.buyerCompanyName}</span> ({link.buyerEmail}). Their
          bid categories follow this account's Part Categories.
        </p>
      );
    case "INVITED":
      return (
        <p className="text-sm text-gray-700">
          Invite sent to <span className="font-medium">{link.inviteEmail}</span> on {formatDate(link.inviteSentAt)}.
          It expires on {formatDate(link.inviteExpiresAt)}.
        </p>
      );
    case "INVITE_EXPIRED":
      return (
        <p className="text-sm text-gray-700">
          The invite to <span className="font-medium">{link.inviteEmail}</span> expired on{" "}
          {formatDate(link.inviteExpiresAt)}. Resend it below.
        </p>
      );
    case "REVOKED":
      return (
        <p className="text-sm text-gray-700">
          {link.buyerCompanyName ?? "This buyer"} no longer has access to your trade vehicles. Re-grant them from
          Buyers in PartPro Trade.
        </p>
      );
    default:
      return (
        <p className="text-sm text-gray-700">
          Email the buyer an invite to link their PartPro Trade account with this account. Once they accept, they
          can bid on this account's Part Categories that you offer on PartPro Trade.
        </p>
      );
  }
}

export function AccountPartProTradeModal({
  open,
  setOpen,
  accountId,
  defaultEmail,
}: AccountPartProTradeModalProps) {
  const { link, invite, unlink } = useAccountPartProTrade(accountId, open, "AccountPartProTradeModal");
  const { addNotification } = useNotification();
  const [email, setEmail] = useState("");

  const data = link.data?.data;

  useEffect(() => {
    if (open) setEmail(data?.inviteEmail ?? defaultEmail ?? "");
  }, [open, data?.inviteEmail, defaultEmail]);

  const notifyError = (err: unknown) =>
    addNotification({
      variant: "error",
      primaryText: "PartPro Trade",
      secondaryText: err instanceof Error ? err.message : "Something went wrong",
    });

  const handleInvite = () => {
    invite.mutate(
      { email: email.trim() },
      {
        onSuccess: (res) =>
          addNotification({ variant: "success", primaryText: res.message ?? "PartPro Trade invite sent" }),
        onError: notifyError,
      }
    );
  };

  const handleUnlink = () => {
    unlink.mutate(undefined, {
      onSuccess: () => addNotification({ variant: "success", primaryText: "PartPro Trade buyer unlinked" }),
      onError: notifyError,
    });
  };

  const canInvite = data && data.status !== "LINKED" && data.status !== "REVOKED";
  const canUnlink = data && data.status !== "NOT_INVITED";
  const busy = invite.isLoading || unlink.isLoading;

  return (
    <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
      <h2 className="text-xl font-semibold mb-6">PartPro Trade</h2>

      {link.isLoading ? (
        <LoadingWheel />
      ) : link.isError || !data ? (
        <p className="text-sm text-red-600">
          {link.error instanceof Error ? link.error.message : "PartPro did not return this account's trade link."}
        </p>
      ) : (
        <div className="space-y-6">
          <LinkSummary link={data} />

          {data.status === "LINKED" && (
            <div>
              <p className="text-sm font-medium text-gray-700">Can bid on ({data.categories.length})</p>
              {data.categories.length === 0 ? (
                <p className="mt-1 text-sm text-gray-500">
                  None yet - add Part Categories to this account that you offer in PartPro Trade settings.
                </p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.categories.map((category) => (
                    <span
                      key={category.externalId ?? category.name}
                      className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                    >
                      {category.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {canInvite && (
            <div>
              <label htmlFor="partProTradeInviteEmail" className="block text-sm font-medium text-gray-700">
                Buyer email
              </label>
              <input
                id="partProTradeInviteEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            {canUnlink && (
              <ButtonInput
                label={data.status === "LINKED" || data.status === "REVOKED" ? "Unlink" : "Cancel Invite"}
                isSubmit={false}
                classes="!bg-red-500 hover:!bg-red-600"
                onClick={handleUnlink}
                isLoading={unlink.isLoading}
                disabled={busy}
              />
            )}
            {canInvite && (
              <ButtonInput
                label={data.status === "NOT_INVITED" ? "Send Invite" : "Resend Invite"}
                isSubmit={false}
                classes=""
                onClick={handleInvite}
                isLoading={invite.isLoading}
                disabled={busy || !email.trim()}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
